import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Row, Col, Card, CardBody, Form, FormGroup,
    Label, Input, Button, InputGroup, InputGroupText
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

const CreditNoteCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [reductionType, setReductionType] = useState("amount");

    // AsyncSelect key - client change aana invoice select reset aagum
    const invoiceSelectRef = useRef(null);

    const today = dayjs();

    const [formData, setFormData] = useState({
        invoice_id: "",
        credit_note_no: "",
        date: today,
        amount: "",
        reduced_kg: "",
        gst_percentage: 0,
        reason: ""
    });

    useEffect(() => {
        fetchClients();
    }, []);

    // ─── Fetch Clients (normal select - clients count manageable) ───
    const fetchClients = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: "/clients/list.php" });
            if (res?.success) {
                const clientOptions = res?.data?.map(c => ({
                    value: c?.id,
                    label: c?.client?.name?.toUpperCase() || "UNKNOWN CLIENT"
                })) || [];
                setClients(clientOptions);
            }
        } catch (err) {
            console.error("Client fetch error:", err);
            toast.error("Failed to load clients");
        } finally {
            dispatch(loadingFalse());
        }
    };

    // ─── Client change handler ───
    const handleClientChange = (opt) => {
        const clientId = opt?.value || null;
        setSelectedClientId(clientId);
        setSelectedInvoice(null);

        // ✅ Functional update - stale closure fix
        setFormData(prev => ({
            ...prev,
            invoice_id: "",
            amount: "",
            reduced_kg: "",
            reason: "",
            gst_percentage: 0
        }));

        // AsyncSelect reset
        if (invoiceSelectRef.current) {
            invoiceSelectRef.current.clearValue();
        }
    };

    // ─── AsyncSelect loadOptions - server side search ───
    // Called automatically by AsyncSelect when user types
    const loadInvoiceOptions = async (inputValue) => {
        if (!selectedClientId) return [];

        try {
            // inputValue empty aana → recent 20 invoices show (defaultOptions)
            // inputValue irundha → search filter apply
            const searchParam = inputValue ? `&search=${encodeURIComponent(inputValue)}` : "";
            const res = await Handler({
                method: "GET",
                url: `/invoice/list.php?client_id=${selectedClientId}${searchParam}&limit=20&sort=DESC`
            });

            if (res?.success && res?.data?.length > 0) {
                return res.data.map(i => ({
                    value: i.invoice.id,
                    label: `${i.invoice.invoice_no} — ₹${parseFloat(i.summary.total_amount || 0).toLocaleString('en-IN')}`,
                    fullData: i
                }));
            }
            return [];
        } catch (err) {
            console.error("Invoice search error:", err);
            return [];
        }
    };

    // ─── Debounce helper - rapid typing la multiple API calls avoid ───
    const debounceLoadOptions = (inputValue) => {
        return new Promise((resolve) => {
            clearTimeout(debounceLoadOptions._timer);
            debounceLoadOptions._timer = setTimeout(async () => {
                const result = await loadInvoiceOptions(inputValue);
                resolve(result);
            }, 400); // 400ms debounce
        });
    };

    // ─── Reduction type toggle ───
    const handleTypeToggle = (type) => {
        setReductionType(type);
        setFormData(prev => ({ ...prev, amount: "", reduced_kg: "" }));
    };

    // ─── Preview calculation ───
    const calculatePreview = () => {
        if (!selectedInvoice) return { reduced: 0, gst: 0, total: 0, base: 0 };

        const subtotal = parseFloat(selectedInvoice.financials?.subtotal || selectedInvoice.summary?.subtotal || 0);
        const totalKg  = parseFloat(selectedInvoice.quantity?.kg || 0);
        const rate     = totalKg > 0 ? subtotal / totalKg : 0;
        const gstPerc  = parseFloat(formData.gst_percentage || 0);

        let baseAmount    = 0;
        let reducedWeight = 0;

        if (reductionType === "amount") {
            baseAmount    = parseFloat(formData.amount || 0);
            reducedWeight = rate > 0 ? baseAmount / rate : 0;
        } else {
            reducedWeight = parseFloat(formData.reduced_kg || 0);
            baseAmount    = reducedWeight * rate;
        }

        const gstAmt     = (baseAmount * gstPerc) / 100;
        const totalCredit = baseAmount + gstAmt;

        return {
            base:    baseAmount.toFixed(2),
            reduced: reducedWeight.toFixed(2),
            gst:     gstAmt.toFixed(2),
            total:   totalCredit.toFixed(2)
        };
    };

    const preview = calculatePreview();

    // ─── Form Submit ───
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.credit_note_no) return toast.error("Credit Note Number is required!");
        if (!formData.date)           return toast.error("Date is required!");
        if (!formData.invoice_id)     return toast.error("Invoice is required!");
        if (!formData.reason?.trim()) return toast.error("Reason is required!");

        const val = parseFloat(reductionType === "amount" ? formData.amount : formData.reduced_kg);
        if (!val || val <= 0) return toast.error(`${reductionType === "amount" ? "Amount" : "Weight"} is required!`);

        if (selectedInvoice) {
            const maxSubtotal = parseFloat(selectedInvoice.financials?.subtotal || selectedInvoice.summary?.subtotal || 0);
            const maxWeight   = parseFloat(selectedInvoice.quantity?.kg || 0);

            if (reductionType === "amount" && val > maxSubtotal) {
                return toast.error(`Amount cannot exceed Invoice Subtotal (₹${maxSubtotal.toLocaleString('en-IN')})`);
            }
            if (reductionType === "kg" && val > maxWeight) {
                return toast.error(`Weight cannot exceed Invoice Quantity (${maxWeight} KG)`);
            }
        }

        const result = await Swal.fire({
            title: 'Confirm Reduction?',
            text: "This will update the invoice and generate a Credit Note.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Apply'
        });

        if (!result.isConfirmed) return;

        dispatch(loadingTrue());
        try {
            const submitData = {
                invoice_id:      formData.invoice_id,
                credit_note_no:  formData.credit_note_no,
                date:            formData.date.format('YYYY-MM-DD'),
                reason:          formData.reason,
                gst_percentage:  formData.gst_percentage,
                ...(reductionType === "amount"
                    ? { amount: formData.amount }
                    : { reduced_kg: formData.reduced_kg }
                )
            };

            const res = await Handler({ method: "POST", url: "/credit_note/create.php", data: submitData });

            if (res?.success) {
                await Swal.fire("Success!", "Invoice updated and Credit Note generated.", "success");
                navigate("/creditnotes");
            } else {
                toast.error(res?.message || "Failed to process Credit Note");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to process Credit Note");
        } finally {
            dispatch(loadingFalse());
        }
    };

    // ─── AsyncSelect custom styles ───
    const asyncSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '8px',
            borderColor: state.isFocused ? '#dc3545' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(220,53,69,.15)' : 'none',
            '&:hover': { borderColor: '#dc3545' }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#dc3545' : state.isFocused ? '#fff5f5' : 'white',
            color: state.isSelected ? 'white' : '#333',
            fontSize: '0.875rem'
        })
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 p-2">
                    <h3 className="fw-bold text-danger m-0">Bill Reduction (Credit Note)</h3>
                    <Button color="secondary" outline size="sm" onClick={() => navigate(-1)}>Back</Button>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">

                        {/* ── LEFT COLUMN ── */}
                        <Col lg={8}>

                            {/* Credit Note Details Card */}
                            <Card className="border-0 shadow-sm rounded-4 mb-3">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold text-muted text-uppercase small mb-4">Credit Note Details</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">
                                                    Credit Note No <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Ex: CN-2026-001"
                                                    value={formData.credit_note_no}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, credit_note_no: e.target.value }))}
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold">
                                                    Date <span className="text-danger">*</span>
                                                </Label>
                                                <DesktopDatePicker
                                                    value={formData.date}
                                                    maxDate={today}
                                                    onChange={(newValue) => setFormData(prev => ({ ...prev, date: newValue }))}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small",
                                                            required: true,
                                                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px' } }
                                                        }
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Source Information Card */}
                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold text-muted text-uppercase small mb-4">Source Information</h6>
                                    <Row className="g-3">

                                        {/* Client Select - normal select ok (clients count manageable) */}
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">Select Client</Label>
                                                <Select
                                                    options={clients}
                                                    onChange={handleClientChange}
                                                    placeholder="Search Client..."
                                                    isClearable
                                                    styles={asyncSelectStyles}
                                                />
                                            </FormGroup>
                                        </Col>

                                        {/* ✅ Invoice - AsyncSelect with server-side search */}
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">
                                                    Select Invoice
                                                    {selectedClientId && (
                                                        <span className="text-muted fw-normal ms-1" style={{ fontSize: '0.75rem' }}>
                                                            (type to search)
                                                        </span>
                                                    )}
                                                </Label>
                                                <AsyncSelect
                                                    ref={invoiceSelectRef}
                                                    key={selectedClientId} // ✅ client change aana reset
                                                    cacheOptions={false}   // search results cache vendam - always fresh
                                                    defaultOptions={!!selectedClientId} // client select aana auto load recent 20
                                                    loadOptions={debounceLoadOptions}
                                                    isDisabled={!selectedClientId}
                                                    onChange={(opt) => {
                                                        setFormData(prev => ({ ...prev, invoice_id: opt?.value || "" }));
                                                        setSelectedInvoice(opt?.fullData || null);
                                                    }}
                                                    placeholder={selectedClientId ? "Type to search invoices..." : "Select client first"}
                                                    isClearable
                                                    styles={asyncSelectStyles}
                                                    noOptionsMessage={({ inputValue }) =>
                                                        inputValue
                                                            ? `No invoices found for "${inputValue}"`
                                                            : "No invoices available"
                                                    }
                                                    loadingMessage={() => "Searching invoices..."}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="small fw-bold">GST (%)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={formData.gst_percentage}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, gst_percentage: e.target.value }))}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={8}>
                                            <FormGroup>
                                                <Label className="small fw-bold">
                                                    Reason <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Reason for adjustment..."
                                                    value={formData.reason}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── RIGHT COLUMN ── */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: "20px" }}>
                                <CardBody className="p-4">

                                    <h6 className="fw-bold mb-3 text-uppercase small text-muted">Adjustment Method</h6>

                                    {/* Toggle */}
                                    <div className="d-flex mb-4 p-1 bg-light rounded-pill border">
                                        <Button
                                            color={reductionType === "amount" ? "danger" : "light"}
                                            className="flex-fill rounded-pill border-0 py-2 btn-sm fw-bold"
                                            type="button"
                                            onClick={() => handleTypeToggle("amount")}
                                        >
                                            By Amount
                                        </Button>
                                        <Button
                                            color={reductionType === "kg" ? "danger" : "light"}
                                            className="flex-fill rounded-pill border-0 py-2 btn-sm fw-bold"
                                            type="button"
                                            onClick={() => handleTypeToggle("kg")}
                                        >
                                            By Weight
                                        </Button>
                                    </div>

                                    {/* Selected Invoice Info */}
                                    {selectedInvoice && (
                                        <div className="bg-light p-3 rounded-3 mb-4 border">
                                            <div className="d-flex justify-content-between mb-1 small text-muted">
                                                <span>Invoice No:</span>
                                                <span className="fw-bold text-dark">{selectedInvoice.invoice?.invoice_no}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-1 small text-muted">
                                                <span>Subtotal (Taxable):</span>
                                                <span className="fw-bold text-dark">
                                                    ₹{parseFloat(
                                                        selectedInvoice.financials?.subtotal ||
                                                        selectedInvoice.summary?.subtotal ||
                                                        selectedInvoice.summary?.total_amount || 0
                                                    ).toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small text-muted">
                                                <span>Current Qty:</span>
                                                <span className="fw-bold text-dark">
                                                    {parseFloat(selectedInvoice.quantity?.kg || 0).toLocaleString('en-IN')} KG
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Amount / KG Input */}
                                    <FormGroup className="mb-4">
                                        <Label className="small fw-bold text-danger">
                                            {reductionType === "amount" ? "REDUCTION AMOUNT (₹)" : "REDUCTION WEIGHT (KG)"}
                                        </Label>
                                        <InputGroup size="lg">
                                            <InputGroupText className="bg-white text-danger fw-bold">
                                                {reductionType === "amount" ? "₹" : "KG"}
                                            </InputGroupText>
                                            <Input
                                                type="number"
                                                required
                                                placeholder="0.00"
                                                step="any"
                                                min="0"
                                                value={reductionType === "amount" ? formData.amount : formData.reduced_kg}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    [reductionType === "amount" ? "amount" : "reduced_kg"]: e.target.value
                                                }))}
                                            />
                                        </InputGroup>
                                    </FormGroup>

                                    {/* Preview */}
                                    {selectedInvoice && (formData.amount || formData.reduced_kg) && (
                                        <div className="bg-white border rounded-3 p-3 mb-4">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-muted">Base Credit:</span>
                                                <span className="fw-bold">₹{parseFloat(preview.base).toLocaleString('en-IN')}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-muted">Est. Reduced Qty:</span>
                                                <span className="fw-bold">{preview.reduced} KG</span>
                                            </div>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-muted">GST ({formData.gst_percentage}%):</span>
                                                <span className="fw-bold text-primary">₹{parseFloat(preview.gst).toLocaleString('en-IN')}</span>
                                            </div>
                                            <hr className="my-2" />
                                            <div className="d-flex justify-content-between">
                                                <span className="fw-bold">Total Credit:</span>
                                                <span className="fw-bold text-success fs-6">₹{parseFloat(preview.total).toLocaleString('en-IN')}</span>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        color="danger"
                                        block
                                        className="py-3 fw-bold shadow-sm rounded-3"
                                        type="submit"
                                        disabled={!formData.invoice_id || !formData.credit_note_no}
                                    >
                                        APPLY REDUCTION
                                    </Button>

                                </CardBody>
                            </Card>
                        </Col>

                    </Row>
                </Form>
            </div>
        </LocalizationProvider>
    );
};

export default CreditNoteCreate;